"use client";

import type { CampaignQuestState } from "@/components/QuestsV2/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import * as React from "react";
import { type BadgeTooltipData, DisplayBadge } from "./display-badge";
import { GemsBadge, PointsBadge } from "./quest-badges";

export interface QuestBadgeConfig {
  imageUrl: string;
  tooltipData?: BadgeTooltipData;
  alt: string;
}

// Quest Card Component
const QuestCard = React.forwardRef<
  HTMLDivElement,
  {
    quest: CampaignQuestState;
    backgroundImage?: string;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    badge?: QuestBadgeConfig;
    onBadgeHover?: (quest: CampaignQuestState) => void;
    onBadgeClick?: (quest: CampaignQuestState) => void;
  }
>(
  (
    {
      quest,
      backgroundImage,
      className,
      onClick,
      disabled,
      badge,
      onBadgeHover,
      onBadgeClick,
    },
    ref,
  ) => {
    return (
      <Card
        ref={ref}
        variant="frosted"
        backgroundImage={backgroundImage}
        onClick={disabled ? undefined : onClick}
        className={cn(
          "transition-all duration-300 ease-in-out",
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20 cursor-pointer",
          className,
        )}
      >
        <div className="h-full flex flex-col p-4 min-h-56 @container relative">
          {/* Content */}
          <h3
            className={
              "font-suisse text-white leading-tight text-2xl @2xl:text-4xl font-semibold flex items-start justify-between gap-2"
            }
            style={{ textShadow: "0 0 4px rgba(0, 0, 0, 0.7)" }}
          >
            <span>{quest.name}</span>
            {/* Custom Badge */}
            {badge && (
              <DisplayBadge
                imageUrl={badge.imageUrl}
                alt={badge.alt}
                tooltipData={badge.tooltipData}
                size={64}
                className="shrink-0 w-16 h-16 @2xl:w-16 @2xl:h-16"
                animated={true}
                quest={quest}
                onBadgeHover={onBadgeHover}
                onBadgeClick={onBadgeClick}
              />
            )}
          </h3>
          <p
            className="text-sm flex-1"
            style={{ textShadow: "0 0 4px rgba(0, 0, 0, 0.7)" }}
          >
            {quest.caption}
          </p>
          <div className="flex gap-2 mt-3">
            <PointsBadge points={quest.campaignPoints} />
            <GemsBadge gems={quest.gems} />
            <Button
              variant="default"
              size="icon"
              onClick={disabled ? undefined : onClick}
              disabled={disabled}
              className="ml-auto"
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    );
  },
);

export { QuestCard };
